<template>
  <div class="container">
    <div class="p-booking__inner-wrapper">
      <Loader v-if="isLoading"></Loader>
      <Alert
        v-if="errorMsgs.length"
        type="danger"
        :messages="errorMsgs"></Alert>

      <div class="p-booking__title pt-4">
        {{ dictionary.paymentTitle }}
      </div>
      <div class="p-booking__title-desc mb-5">
        {{ dictionary.paymentDescription }}
      </div>

      <div class="b-payment-details">
        <div class="b-payment-details__body">
          <div class="b-payment-details__main">
            <div class="b-payment-details__form">
              <div class="b-payment-details__details">
                <form autocomplete="off" @submit.prevent="onSubmitForm">
                  <PaymentDetails
                    v-if="showPaymentDetails"
                    @change-input="onChangeInput" />
                  <UserProfile
                    v-if="showUserProfile"
                    @click-edit="onClickEdit" />
                  <button
                    :id="buttonPaymentSubmitId"
                    type="submit"
                    class="d-none"
                    :disabled="buttonSubmitIsDisabled"></button>
                </form>
              </div>

              <div class="b-payment-details__form__aside">
                <CartSummary />
                <AddCreditVoucher />
              </div>

              <div class="b-payment-details__terms">
                <div>
                  <div class="custom-chck custom-chck--additional-style">
                    <input
                      id="user-terms"
                      type="checkbox"
                      :class="{ 'form-control--error': v$.terms.$error }"
                      :checked="terms"
                      @change="terms = $event.target.checked"
                      @blur="v$.terms.$touch" />
                    <label for="user-terms">
                      <span class="custom-chck__span">
                        <div class="custom-chck__text">
                          <em>{{ dictionary.termsHyperLabel }}*</em>
                          <span v-html="dictionary.termsLabel"></span>
                        </div>
                      </span>
                    </label>
                  </div>
                  <div v-if="v$.terms.$error">
                    <div
                      v-if="v$.terms.checked.$invalid"
                      class="field-validation-error">
                      {{ dictionary.termsRequired }}
                    </div>
                  </div>
                </div>
              </div>
              <RegisterContest v-if="showContestRegistration" />
            </div>
          </div>
          <div class="b-payment-details__aside">
            <CartSummary />
            <AddCreditVoucher />
          </div>
        </div>
      </div>
      <Otp
        ref="otp"
        :dictionary="otpSettings.dictionary"
        :configuration="otpSettings.configuration"
        @success="triggerSubmit"
        @otp-blocked-error="handleBlockedErrorOtp"
        @loading="
          (value) => {
            isLoading = value
          }
        " />
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeMount,
  onMounted,
  ref,
} from 'vue'
import PaymentDetails from './../../components/Booking/Step5/PaymentDetails.vue'
import RegisterContest from './../../components/Booking/Step5/RegisterContest.vue'
import UserProfile from './../../components/Booking/Step5/UserProfile.vue'
import Loader from './../../components/Shared/Loader.vue'
import Alert from './../../components/Shared/Alert.vue'
import { useBookingStore } from './../../store/BookingStore'
import useVuelidate from '@vuelidate/core'
import CartSummary from './../../components/Booking/Shared/CartSummary.vue'
import AddCreditVoucher from './../../components/AddCreditVoucher.vue'
import Otp from './../../components/Otp.vue'
import postRequestHandler from './../../../lib/postRequestHandler'
import getErrorMessage from './../../../lib/getErrorMessage'
import constants from '../../../lib/constants'
import * as lib from './../../../lib/lib'

const store = useBookingStore()

const { configuration, dictionary } =
  getCurrentInstance().appContext.config.globalProperties

const buttonPaymentSubmitId = constants.buttonPaymentSubmitId

// otp
const otp = ref(null)
const otpSettings = ref(null)
const otpVerificationCode = ref(null)
const handleBlockedErrorOtp = () => {
  lib.scrollToTop()
}
const triggerSubmit = async function (verificationCode) {
  otpVerificationCode.value = verificationCode
  await nextTick()
  onSubmitForm()
}

const isLoading = ref(false)
const errorMsgs = ref([])
const editProfileEnabled = ref(false)

const terms = computed({
  // getter
  get() {
    return store.bookingProgress.paymentDetails.terms
  },
  // setter
  set(value) {
    store.setPaymentField({ field: 'terms', value })
    onChangeInput()
  },
})

const showPaymentDetails = computed(() => {
  return store.accountIsInValidForPayment || editProfileEnabled.value
})
const showContestRegistration = computed(() => {
  return store.bookingProgress.contestId
})

const showUserProfile = computed(() => {
  return !store.accountIsInValidForPayment && !editProfileEnabled.value
})

const buttonSubmitIsDisabled = computed(() => {
  return isLoading.value || store.bookingProgress.paymentDetails.invalid
})

const rules = computed(() => ({
  terms: {
    checked: (value) => value === true,
  },
}))

const v$ = useVuelidate(rules, { terms })
onBeforeMount(() => {
  const { otp } = lib.getData('bookingData')
  otpSettings.value = otp
})
onMounted(() => {
  checkLogin()
})

const onChangeInput = () => {
  store.setPaymentField({
    field: 'invalid',
    value: v$.value.$invalid,
  })
}

const onClickEdit = async () => {
  editProfileEnabled.value = !editProfileEnabled.value
  await nextTick()
  lib.scrollToElem(jQuery('.b-payment-details'), 0)
}

const onSubmitForm = async () => {
  v$.value.$validate()

  if (v$.value.$invalid) {
    await nextTick()
    lib.scrollToValidation()
    return false
  }

  const { firstName, lastName, email, mobilePhone, registerContest, invalid } =
    store.bookingProgress.paymentDetails

  if (invalid) {
    return false
  }

  removeErrors()
  if (store.accountIsInValidForPayment && !otpVerificationCode.value) {
    otp.value.start(email)
    return false
  }

  isLoading.value = true

  const postData = {
    firstName: firstName,
    lastName: lastName,
    email: email,
    mobilePhone: mobilePhone,
    acceptTerms: terms.value,
    amount: store.getTotalCart,
    culture: document.documentElement.lang,
    verificationCode: otpVerificationCode.value,
    registerContest: registerContest,
    userSessionId: store.bookingProgress.usersessionId,
  }

  await postRequestHandler({
    postData,
    endpoint: configuration.endpointBookingSubmitData,
    successCallback: handleSuccess,
    errorCallback: handleError,
    captchaAction: configuration.submitOrdercaptchaAction,
    refreshTokenHandler: configuration.refresh,
  })
}

const handleSuccess = (response) => {
  isLoading.value = false
  removeErrors()

  const { redirectUrl } = response.data

  if (redirectUrl) {
    window.location.assign(redirectUrl)
  }
}

const checkLogin = async () => {
  removeErrors()
  isLoading.value = true

  const postData = {}

  await postRequestHandler({
    postData,
    endpoint: configuration.endpointCheckLogin,
    successCallback: checkLoginHandleSuccess,
    errorCallback: handleError,
    captchaAction: configuration.checkLogincaptchaAction,
    refreshTokenHandler: configuration.refresh,
  })

  isLoading.value = false
}

const checkLoginHandleSuccess = (response) => {
  isLoading.value = false
  removeErrors()

  const { accountData } = response.data

  if (!accountData) {
    return false
  }

  store.setAccountData(accountData)
  store.setPaymentDetails(accountData)
}

const handleError = async (messages) => {
  isLoading.value = false

  errorMsgs.value = getErrorMessage(messages, dictionary.defaultErrorMessage)

  await nextTick()
  lib.scrollToError()
}

const removeErrors = () => {
  errorMsgs.value = []
}
</script>

<style lang="scss" scoped>
.loader {
  position: fixed;
}
</style>
